import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth, db } from "../config/firebase";
import { doc, setDoc, collection } from "firebase/firestore";
import Swal from "sweetalert2";
import "./login.css";

export default function Login() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    middleName: "",
    lastName: "",
    phoneNumber: "",
    birthdate: "",
  });
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false); // Track if user is in forgot password mode
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState(""); // Separate state for forgot password email

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.classList.toggle("right-panel-active", isSignUp);
    }
  }, [isSignUp]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isSignUp) {
        setIsSignUp(false); // Reset only for larger screens
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSignUp]);

  useEffect(() => {
    // Lock scroll when modal is open
    if (isForgotPassword) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isForgotPassword]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      title: "Error!",
      text: message,
      icon: "error",
      confirmButtonText: "Okay",
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const { email, password } = formData;
    try {
      await signInWithEmailAndPassword(auth, email, password);
      const redirectPath = localStorage.getItem("redirectPath");
      navigate(redirectPath || "/dashboard");
      localStorage.removeItem("redirectPath");
    } catch (error) {
      const messages = {
        "auth/user-not-found": "User not found. Please check the email or sign up.",
        "auth/wrong-password": "Incorrect password. Please try again.",
        "auth/invalid-email": "Invalid email address. Please enter a valid email.",
      };
      showErrorAlert(messages[error.code] || "Login failed. Please try again.");
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    const { email, password, firstName, middleName, lastName, phoneNumber, birthdate } = formData;
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userDocRef = doc(db, "Users", user.uid);
      const studentDocRef = doc(collection(userDocRef, "Students"));
      const enrollmentDocRef = doc(collection(userDocRef, "Enrollments"));

      await Promise.all([
        setDoc(userDocRef, { firstName, middleName, lastName, email, phoneNumber, birthdate }),
        setDoc(studentDocRef, { firstName, middleName, lastName, email, phoneNumber, birthdate, coursesComplete: [] }),
        setDoc(enrollmentDocRef, {}),
      ]);

      navigate("/dashboard");
    } catch (error) {
      const messages = {
        "auth/email-already-in-use": "This email is already in use. Please try a different email.",
        "auth/invalid-email": "Invalid email address. Please enter a valid email.",
      };
      showErrorAlert(messages[error.code] || "Sign up failed. Please try again.");
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      showErrorAlert("Please enter your email address.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      Swal.fire({
        title: "Password Reset Email Sent!",
        text: "Please check your inbox for the password reset link.",
        icon: "success",
        confirmButtonText: "Okay",
      });
      setIsForgotPassword(false); // Hide the forgot password form after success
    } catch (error) {
      showErrorAlert("Failed to send password reset email. Please try again.");
    }
  };
  const handleForgotPasswordChange = (e) => {
    setForgotPasswordEmail(e.target.value);
  };

  return (
    <div className="login-container">
      {/* For Larger Screens */}
      <div className="container" ref={containerRef}>
        <div className="form-container sign-up-container">
          <form onSubmit={handleSignUp}>
            <h1>Create Account</h1>
            <input
              type="text"
              id="firstName"
              name="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleChange}
            />
            <input
              type="text"
              id="middleName"
              name="middleName"
              placeholder="Middle Name"
              value={formData.middleName}
              onChange={handleChange}
            />
            <input
              type="text"
              id="lastName"
              name="lastName"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleChange}
            />
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
            />
            <input
              type="password"
              id="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
            />
            <input
              type="text"
              id="phoneNumber"
              name="phoneNumber"
              placeholder="Phone Number"
              value={formData.phoneNumber}
              onChange={handleChange}
            />
            <input
              type="text"
              id="birthdate"
              name="birthdate"
              placeholder="Birthdate"
              onFocus={(e) => (e.target.type = "date")}
              onBlur={(e) => (e.target.type = "text")}
              value={formData.birthdate}
              onChange={handleChange}
            />
            <button type="submit">Sign Up</button>
          </form>
        </div>

        <div className="form-container sign-in-container">
          <form onSubmit={handleLogin}>
            <h1>Sign In</h1>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
            />
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
            />
            <button type="submit">Sign In</button>
            <button
              type="button"
              className="link"
              onClick={() => setIsForgotPassword(true)} // Show forgot password form
            >
              Forgot Password?
            </button>
          </form>
        </div>

        {/* Forgot Password Form */}
        {isForgotPassword && (
  <div className="forgot-password-modal">
    <div className="forgot-password-content">
      <h2>Reset Your Password</h2>
      <input
        type="email"
        placeholder="Enter your email"
        value={forgotPasswordEmail}
        onChange={handleForgotPasswordChange}
      />
      <div className="forgot-password-button-container">
        <button type="button" onClick={handleForgotPassword}>
          Send Reset Link
        </button>
        <button type="button" onClick={() => setIsForgotPassword(false)}>
          Back to Login
        </button>
      </div>
    </div>
  </div>
)}



        <div className="overlay-container">
          <div className="overlay">
            <div className="overlay-panel overlay-left">
              <h1>Welcome Back!</h1>
              <p>To keep connected with us, please log in with your personal info</p>
              <button className="ghost" onClick={() => setIsSignUp(false)}>
                Sign In
              </button>
            </div>
            <div className="overlay-panel overlay-right">
              <h1>Hello, Friend!</h1>
              <p>Enter your personal details and start your journey with us</p>
              <button className="ghost" onClick={() => setIsSignUp(true)}>
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* For Smaller Screens */}
      <div className="small-screen-form">
  <div className={`formHolder ${isSignUp ? "signup" : "login"}`}>
    {isSignUp ? (
      <form onSubmit={handleSignUp}>
        <h3>Sign Up</h3>
        {["firstName", "middleName", "lastName", "email", "password", "phoneNumber", "birthdate"].map((field, index) => (
          <input
            key={index}
            type={field === "password" ? "password" : field === "birthdate" ? "text" : "text"}
            name={field}
            placeholder={field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, " $1")}
            value={formData[field]}
            onChange={handleChange}
            onFocus={field === "birthdate" ? (e) => (e.target.type = "date") : undefined}
            onBlur={field === "birthdate" ? (e) => (e.target.type = "text") : undefined}
          />
        ))}
        <button type="submit">Sign Up</button>
        <button type="button" className="link" onClick={() => setIsSignUp(false)}>
          Already have an account?
        </button>
      </form>
    ) : (
      <form onSubmit={handleLogin}>
        <h3>Login</h3>
        {["email", "password"].map((field, index) => (
          <input
            key={index}
            type={field === "password" ? "password" : "email"}
            name={field}
            placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
            value={formData[field]}
            onChange={handleChange}
          />
        ))}
        <button type="submit">Login</button>
        <button type="button2" className="link" onClick={() => setIsSignUp(true)}>
          Don't have an account?
        </button>
        <button
          type="button2"
          className="link"
          onClick={() => setIsForgotPassword(true)}
        >
          Forgot Password?
        </button>
      </form>
    )}
  </div>
</div>
            {/* Forgot Password Modal */}
            {isForgotPassword && (
  <div className="forgot-password-modal">
    <div className="forgot-password-content">
      <h2>Reset Your Password</h2>
      <input
        type="email"
        placeholder="Enter your email"
        value={forgotPasswordEmail}
        onChange={handleForgotPasswordChange}
      />
      <div className="forgot-password-button-container">
        <button type="button" onClick={handleForgotPassword}>
          Send Reset Link
        </button>
        <button type="button" onClick={() => setIsForgotPassword(false)}>
          Back to Login
        </button>
      </div>
    </div>
  </div>
)}


    </div>
  );
}
